import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Hotel, Check, ArrowRight, Star, Globe, Leaf,
  CreditCard, Users, Award
} from 'lucide-react';

const HospitalityPage = () => {
  return (
    <div data-testid="hospitality-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <Hotel className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Hotele & Turizëm</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Karikimi EV si Amenity Premium
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Tërheqni udhëtarë modernë dhe të vetëdijshëm për mjedisin duke ofruar karikimin e automjeteve 
              elektrike si shërbim premium në hotelin ose resortin tuaj.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/contact">
                  Kërkoni Ofertë
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '73%', label: 'Udhëtarë kërkojnë karikues' },
              { value: '2x', label: 'Më gjatë qëndrojnë' },
              { value: '35%', label: 'Rritje rezervimesh' },
              { value: '5★', label: 'Vlerësime më të larta' }
            ].map((stat, index) => (
              <div key={index}>
                <p className="text-3xl md:text-4xl font-bold text-blue-600 font-['Inter_Tight']">{stat.value}</p>
                <p className="text-slate-500 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1762781838321-444256608b2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxyZXNvcnQlMjBob3RlbCUyMHBhbG0lMjB0cmVlcyUyMHdhbGt3YXklMjBzdW5ueXxlbnwwfHx8fDE3NzA5NDcyMDR8MA&ixlib=rb-4.1.0&q=85"
                alt="Resort me palma dhe karikues EV"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-lime-400 text-slate-900 rounded-2xl p-5 shadow-lg">
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6" />
                  <span className="font-bold">Eco-Certified</span>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Përse Karikuesit EV për Hotelin Tuaj?
              </h2>
              
              <ul className="space-y-4">
                {[
                  'Tërheqni segmentin në rritje të udhëtarëve me EV',
                  'Dallohuni nga konkurrenca me shërbim premium',
                  'Rrisni vlerësimet në booking platforms',
                  'Krijoni të ardhura shtesë nga karikimi',
                  'Përmbushni kriteret e certifikimeve eco-friendly',
                  'Mbështetni turizmin e qëndrueshëm në Shqipëri'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-slate-900" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Use Cases */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Zgjidhje për Çdo Strukturë
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Hotel, title: 'Hotele Urbane', desc: 'Karikues në garazhin e hotelit për mysafirët business' },
              { icon: Globe, title: 'Resorte & Spa', desc: 'Karikim i ngadaltë gjatë qëndrimit të gjatë' },
              { icon: Leaf, title: 'Eco-Lodge', desc: 'Përforconi imazhin e qëndrueshmërisë' }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] mb-2">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Bëhuni Hotel EV-Ready
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Kontaktoni ekipin tonë për një vlerësim të nevojave dhe zgjidhje të personalizuar.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">
                Kontaktoni Tani
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospitalityPage;
